import * as React from 'react';
import { CLIENT_TONE_COLORS, CLIENT_TONE_DESCRIPTIONS, type ClientTone } from '@revont/shared-types';
import { Tooltip } from './Tooltip';

export type ToneChipSize = 'xs' | 'sm';

export interface ToneChipProps {
  /** Per-suggestion client tone classification. Renders nothing if null/undefined. */
  tone: ClientTone | null | undefined;
  /** Size variant: `xs` for overlay (default), `sm` for transcript view */
  size?: ToneChipSize;
  /** Additional CSS classes appended to the chip */
  className?: string;
}

const sizeClasses: Record<ToneChipSize, string> = {
  xs: 'text-[10px] px-1.5 py-0.5',
  sm: 'text-xs px-2 py-0.5',
};

/** Capitalize first letter (e.g. `hesitant` → `Hesitant`). */
function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Static pill displaying a per-utterance client tone (hesitant, confident, etc.).
 * Color tokens come from `CLIENT_TONE_COLORS`; tooltip text from `CLIENT_TONE_DESCRIPTIONS`.
 * Returns `null` for null/undefined tone so callers can pass through optional values.
 */
export function ToneChip({ tone, size = 'xs', className = '' }: ToneChipProps): React.JSX.Element | null {
  if (!tone) return null;

  const colors = CLIENT_TONE_COLORS[tone];
  const description = capitalize(CLIENT_TONE_DESCRIPTIONS[tone]);
  const label = capitalize(tone);

  const style: React.CSSProperties = {
    backgroundColor: colors.bg,
    color: colors.text,
    borderColor: colors.border,
  };

  return (
    <Tooltip content={description}>
      <span
        className={`inline-flex items-center font-medium rounded-full border ${sizeClasses[size]} ${className}`}
        style={style}
      >
        {label}
      </span>
    </Tooltip>
  );
}

export default ToneChip;
